import { useMemo } from 'react';
import { Import, FilePenLine, Calendar } from 'lucide-react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router';

import {
  EscrowDashboardCard,
  type IEscrowDashboardItemViewModel,
  type IInfoListItem,
} from '@/widgets/EscrowDashboardCard';
import { softwareEscrowStatusMap } from 'src/shared/mappings/softwareEscrowStatusMap';

import { useDashboardItem } from '../model';

function toViewModel(raw: ReturnType<typeof useDashboardItem>): IEscrowDashboardItemViewModel {
  const { lastDeposit, parties, ...rest } = raw;
  return {
    ...rest,
    lastDeposit,
    parties: parties.map(({ id, name, logo, isNew, partyType }) => ({
      id,
      name,
      logo,
      isNew,
      partyType,
    })),
  };
}

const DashboardItem = () => {
  const raw = useDashboardItem();
  const navigate = useNavigate();
  const { search } = useLocation();

  const viewModel = useMemo(() => toViewModel(raw), [raw]);

  const handleInvitationClick = (id: string) =>
    navigate(`/software-escrows/invitations/${id}${search}`, { replace: true });

  const infoListItems: IInfoListItem[] = useMemo(
    () => [
      {
        label: 'Last deposit',
        value: viewModel.lastDeposit ? dayjs(viewModel.lastDeposit).format('DD MMM, YYYY') : undefined,
        emptyValue: 'No deposits',
        icon: Import,
      },
      {
        label: 'Agreement form',
        value: viewModel.agreementForm,
        emptyValue: '',
        icon: FilePenLine,
      },
      {
        label: 'Created on',
        value: dayjs(viewModel.createdAt).format('DD MMM, YYYY'),
        emptyValue: '',
        icon: Calendar,
      },
    ],
    [viewModel.lastDeposit, viewModel.agreementForm, viewModel.createdAt],
  );

  return (
    <EscrowDashboardCard.Root
      viewModel={viewModel}
      basePath="/software-escrows"
      statusMap={softwareEscrowStatusMap}
      onInvitationClick={handleInvitationClick}
      infoListItems={infoListItems}
    >
      {viewModel.escrowInvitationRejected ? (
        <EscrowDashboardCard.Rejected />
      ) : (
        <EscrowDashboardCard.Card>
          <EscrowDashboardCard.ContentWrapper>
            <div className="p-4 sm:p-6">
              <EscrowDashboardCard.Header />
              <EscrowDashboardCard.Parties />
              <EscrowDashboardCard.InfoList />
            </div>
          </EscrowDashboardCard.ContentWrapper>
        </EscrowDashboardCard.Card>
      )}
    </EscrowDashboardCard.Root>
  );
};

export default DashboardItem;
